import { useNavigate } from 'react-router-dom';
import { ChevronRight, ClipboardCheck, Inbox, ListTodo, MailQuestion } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { AntwortenCard } from './dashboard/AntwortenCard';
import { CustomersCard } from './dashboard/CustomersCard';
import { FollowupsCard } from './dashboard/FollowupsCard';
import { Kachel } from './dashboard/Kachel';
import { QuickActions } from './dashboard/QuickActions';
import { StatsRow } from './dashboard/StatsRow';
import { TasksCard } from './dashboard/TasksCard';
import { useDashboardData } from './dashboard/useDashboardData';

/** Die vollständige Arbeitsfläche der Freigaben; *Heute* zeigt nur ihre Zahl. */
const FREIGABEN = '/akquise?reiter=freigaben';

/**
 * *Heute* beantwortet die Frage, mit der man morgens hereinkommt: Was ist neu,
 * was ist liegengeblieben, wie steht es um den Bestand.
 *
 * Zahlen allein beantworteten sie nur halb — „drei fällige Aufgaben“ hieß
 * immer noch, auf die Aufgabenseite zu wechseln, um zu sehen, welche. Deshalb
 * stehen unter den Kacheln die Listen selbst, und ganz oben der Schnellzugriff
 * auf das, was man von hier aus anlegen will.
 *
 * Zwei Arbeitslisten, die früher eigene Menüpunkte waren, enden hier: die
 * Antworten, die sich gleich einordnen lassen, und die Freigaben — die aber nur
 * als Zahl. 25 fällige Freigaben sind eine Arbeitsfläche für sich, und die
 * liegt im Akquise-Reiter „Freigaben“; neben Antworten, Aufgaben und
 * Kennzahlen wäre sie zu viel für eine Seite zum Hereinschauen.
 *
 * Die früheren Knöpfe „Heute freigeben“ und „Jetzt senden“ sind weg — sie
 * gehörten zur Sequenz-Maschine. Der Postfach-Abruf läuft von selbst im
 * Hintergrund und braucht keinen Auslöser in der Oberfläche.
 */
export function DashboardPage() {
  const navigate = useNavigate();
  const { data, error, reload } = useDashboardData();

  if (error) {
    return (
      <Card className="border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
        {error}
      </Card>
    );
  }

  if (!data) {
    return <p className="text-sm text-muted-foreground">Lade …</p>;
  }

  const { digest } = data;
  const zuTun =
    digest.unreadMessages +
      digest.unassignedMessages +
      data.antworten.length +
      digest.tasksDue +
      digest.followupsDue >
    0;

  return (
    <div className="space-y-6">
      <QuickActions onChanged={reload} />

      <section>
        <h2 className="mb-3 text-sm font-semibold">Was ansteht</h2>
        {/* Vier Kacheln: ab `lg` nebeneinander, darunter zweispaltig — zu
            dritt umgebrochen sähe die vierte aus, als gehörte sie nicht dazu. */}
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Kachel
            label="Ungelesene Nachrichten"
            value={digest.unreadMessages}
            icon={Inbox}
            betont={digest.unreadMessages > 0}
            onClick={() => navigate('/posteingang')}
          />
          <Kachel
            label="Nicht zugeordnet"
            value={digest.unassignedMessages}
            icon={Inbox}
            hinweis="Kein Kunde erkannt"
            onClick={() => navigate('/posteingang')}
          />
          <Kachel
            label="Fällige Aufgaben"
            value={digest.tasksDue}
            icon={ListTodo}
            betont={digest.tasksDue > 0}
            dringend={digest.tasksOverdue > 0}
            hinweis={
              digest.tasksOverdue > 0 ? `${digest.tasksOverdue} überfällig` : undefined
            }
            onClick={() => navigate('/aufgaben')}
          />
          <Kachel
            label="Nachfassen fällig"
            value={digest.followupsDue}
            icon={MailQuestion}
            betont={digest.followupsDue > 0}
            hinweis="Erstansprachen ohne Antwort"
            onClick={() => navigate(FREIGABEN)}
          />
        </div>

        <FreigabenZeile faellig={digest.approvalsDue} onClick={() => navigate(FREIGABEN)} />
      </section>

      {zuTun ? (
        <div className="grid items-start gap-4 lg:grid-cols-2">
          <div className="space-y-4">
            <TasksCard tasks={data.tasks} onChanged={reload} />
            <FollowupsCard followups={data.followups} gesamt={digest.followupsDue} />
          </div>
          <AntwortenCard antworten={data.antworten} onChanged={reload} />
        </div>
      ) : (
        <Card className="p-4 text-sm text-muted-foreground">
          Nichts offen — kein ungelesener Posteingang, keine Antwort zum Einordnen, keine
          fälligen Aufgaben, nichts nachzufassen.
        </Card>
      )}

      <CustomersCard customers={data.customers} />

      <StatsRow
        digest={digest}
        kunden={data.customers.filter((k) => k.status === 'aktiv').length}
        pipelines={data.pipelines}
        projekte={data.projects}
      />
    </div>
  );
}

/**
 * Die Freigaben als eine Zeile: wie viele, und wohin man damit geht.
 *
 * Absichtlich keine Liste und kein Knopf, der hier etwas freigibt — die
 * Entscheidung über einzelne Mails braucht den Platz und die Vorschau des
 * Akquise-Reiters.
 */
function FreigabenZeile({ faellig, onClick }: { faellig: number; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="mt-3 flex w-full items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 text-left shadow-sm transition-colors hover:bg-muted/40"
    >
      <ClipboardCheck
        className={faellig > 0 ? 'h-4 w-4 text-brand' : 'h-4 w-4 text-muted-foreground'}
      />
      <span className="min-w-0 flex-1 text-sm">
        {faellig > 0 ? (
          <>
            <span className="font-semibold">
              {faellig} {faellig === 1 ? 'Freigabe' : 'Freigaben'} fällig
            </span>
            <span className="text-muted-foreground"> · Nachfassen und Erstversand</span>
          </>
        ) : (
          <span className="text-muted-foreground">Keine Freigaben fällig</span>
        )}
      </span>
      <span className="flex shrink-0 items-center gap-0.5 text-xs font-medium text-muted-foreground">
        Zu den Freigaben
        <ChevronRight className="h-3.5 w-3.5" />
      </span>
    </button>
  );
}
